
class StickerDrop {
  constructor(container) {
    this.container = container;
    this.section = container.closest('.section') || 
                   container.closest('.section-wrapper') || 
                   container.closest('[data-sticker-drop]') || 
                   container.parentElement?.closest('.section') || 
                   container.parentElement?.closest('.section-wrapper');
    
    this.stickerContainer = this.section?.querySelector('.sticker-container');
    this.activeStickers = [];
    this.isHovering = false;
    this.lastTouchTime = 0;
    this.touchStart = null;
    this.touchMoved = false;
    
    if (!this.section) {
      console.warn('StickerDrop: Could not find section element for container:', container);
      return;
    }
    
    this.enabled = this.section.dataset?.stickerEnabled !== 'false';
    if (!this.enabled) return;
    
    this.stickerDuration = parseInt(this.section.dataset?.stickerDuration) || 2000;
    this.maxStickers = parseInt(this.section.dataset?.maxStickers) || 20;
    
    const stickersData = this.section.dataset?.stickers;
    if (stickersData && stickersData.trim() !== '') {
      this.stickerCollection = stickersData.split(',').map(url => url.trim()).filter(url => url !== '');
    } else {
      this.stickerCollection = [
        "https://cdn.shopify.com/s/files/1/0947/1511/7884/files/frog-sticker.png?v=1755877741",
        "https://cdn.shopify.com/s/files/1/0947/1511/7884/files/mermaids-sticker.png?v=1755877741",
        "https://cdn.shopify.com/s/files/1/0947/1511/7884/files/ocean-sticker.png?v=1755877741",
        "https://cdn.shopify.com/s/files/1/0947/1511/7884/files/squid-sticker.png?v=1755877741"
      ];
    }
    
    if (this.stickerCollection.length === 0) {
      console.warn('StickerDrop: No stickers available from metaobjects');
      this.stickerCollection = ["https://cdn.shopify.com/s/files/1/0947/1511/7884/files/frog-sticker.png?v=1755877741"];
    }
    
    this.init();
  }
  
  init() {
    if (!this.section || !this.enabled) return;
    this.setupEventListeners();
  }
  
  setupEventListeners() {
    let mouseMoveRafId = 0;
    this.section.addEventListener('mousemove', (e) => {
      if (mouseMoveRafId) return;
      mouseMoveRafId = requestAnimationFrame(() => {
        this.section.style.setProperty('--cursor-x', (e.clientX + 20) + 'px');
        this.section.style.setProperty('--cursor-y', (e.clientY - 30) + 'px');
        mouseMoveRafId = 0;
      });
    }, { passive: true });

    this.section.addEventListener('click', (e) => {
      this.handleClick(e);
    });

    this.section.addEventListener('touchstart', (e) => {
      this.handleTouchStart(e);
    }, { passive: true });

    this.section.addEventListener('touchmove', (e) => {
      this.handleTouchMove(e);
    }, { passive: true });

    this.section.addEventListener('touchend', (e) => {
      this.handleTouchEnd(e);
    });
    
    this.section.addEventListener('mouseenter', () => { this.isHovering = true; });
    this.section.addEventListener('mouseleave', () => { this.isHovering = false; });
  }
  
  handleClick(e) {
    if (this.isInteractiveElement(e.target)) return;
    const now = Date.now();
    if (now - this.lastTouchTime < 500) {
      e.preventDefault();
      return;
    }
    
    const rect = this.stickerContainer.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    this.dropSticker(x, y);
    this.playPopSound();
  }
  
  handleTouchStart(e) {
    const touch = e.touches[0];
    if (!touch) return;
    this.touchMoved = false;
    this.touchStart = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
  }

  handleTouchMove(e) {
    const touch = e.touches[0];
    if (!touch || !this.touchStart) return;
    const dx = Math.abs(touch.clientX - this.touchStart.x);
    const dy = Math.abs(touch.clientY - this.touchStart.y);
    if (dx > 8 || dy > 8) {
      this.touchMoved = true;
    }
  }

  handleTouchEnd(e) {
    if (!this.touchStart || this.touchMoved) {
      this.touchStart = null;
      return;
    }

    const now = Date.now();
    this.lastTouchTime = now;

    const touch = e.changedTouches[0];
    if (!touch || !this.stickerContainer) {
      this.touchStart = null;
      return;
    }

    const bounds = this.stickerContainer.getBoundingClientRect();
    const x = touch.clientX - bounds.left;
    const y = touch.clientY - bounds.top;

    this.dropSticker(x, y);
    this.playPopSound();

    this.touchStart = null;
  }
  
  isInteractiveElement(element) {
    const interactiveTags = ['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA'];
    const interactiveClasses = ['button', 'btn', 'link', 'clickable'];
    
    if (interactiveTags.includes(element.tagName)) return true;
    if (element.closest('button, a, input, select, textarea')) return true;
    
    for (const className of interactiveClasses) {
      if (element.classList.contains(className)) return true;
    }
    
    return false;
  }
  
  dropSticker(x, y) {
    if (this.activeStickers.length >= this.maxStickers) {
      this.removeOldestSticker();
    }
    
    const sticker = this.createSticker(x, y);
    this.stickerContainer.appendChild(sticker);
    this.activeStickers.push(sticker);
    
    setTimeout(() => {
      this.fadeOutSticker(sticker);
    }, this.stickerDuration);
  }
  
  createSticker(x, y) {
    const image = document.createElement('img');
    image.src = this.getRandomSticker();
    image.classList.add('sticker');
    image.alt = 'sticker';
    
    image.style.left = `${x}px`;
    image.style.top = `${y}px`;
    image.style.transform = `translate(-50%, -50%)`;
    
    return image;
  }
  
  getRandomSticker() {
    return this.stickerCollection[Math.floor(Math.random() * this.stickerCollection.length)];
  }
  
  fadeOutSticker(sticker) {
    sticker.classList.add('sticker--remove');
    
    setTimeout(() => {
      if (sticker.parentNode) {
        sticker.parentNode.removeChild(sticker);
        this.activeStickers = this.activeStickers.filter(s => s !== sticker);
      }
    }, 500);
  }
  
  removeOldestSticker() {
    if (this.activeStickers.length > 0) {
      const oldestSticker = this.activeStickers.shift();
      this.fadeOutSticker(oldestSticker);
    }
  }
  
  playPopSound() {
    try {
      if (!this._popAudio) {
        this._popAudio = new Audio('https://cdn.shopify.com/s/files/1/0947/1511/7884/files/pop.mp3?v=1755880482');
        this._popAudio.volume = 0.3;
      }
      this._popAudio.currentTime = 0;
      this._popAudio.play().catch(() => {});
    } catch (error) {}
  }
}

(typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout)(() => {
  const stickerContainers = document.querySelectorAll('.sticker-container');
  stickerContainers.forEach(container => {
    new StickerDrop(container);
  });
});

document.addEventListener('shopify:section:load', (event) => {
  const stickerContainer = event.target.querySelector('.sticker-container');
  if (stickerContainer) {
    new StickerDrop(stickerContainer);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  const stickerContainer = event.target.querySelector('.sticker-container');
  if (stickerContainer) {
    stickerContainer.innerHTML = '';
  }
});
